import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getStoreInfo, getCompanyInfo } from '../../../services/authService';

const initialState = {
  companyInfo: null,
  loading: false,
  error: null,
};

// ✅ Async thunk to fetch store info from API (current active endpoint)
export const fetchCompanyInfo = createAsyncThunk(
  'company/fetchCompanyInfo',
  async (_, { rejectWithValue }) => {
    try {
      // ✅ Using store-info endpoint now
      const data = await getStoreInfo();
      if (data) {
        const mappedData = {
          name: data.CompanyName || data.Name || '',
          storeName: data.StoreName || '',
          vat: data.BTWNumber || data.VATNumber || '',
          email: data.Email || '',
          phone: data.PhoneNumber || data.Phone || '',
          website: data.Website || '',
          address: data.Street || data.Address || '',
          city: data.City || '',
          postCode: data.PostCode || '',
          country: data.Country || '',
          iban: data.IBANumber || data.IBAN || data.Iban || '',
          // Store original data as well
          _raw: data
        };
        return mappedData;
      }
      return null;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch company info');
    }
  },
  {
    // ✅ Skip the API call if data is already in the store or a fetch is in progress
    condition: (_, { getState }) => {
      const { companyInfo, loading } = getState().company;
      if (companyInfo || loading) return false;
      return true;
    },
  }
);

// ✅ Selector to get company info with auto-fetch if not loaded
export const selectCompanyInfoWithFetch = (state) => {
  return state.company.companyInfo;
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setCompanyInfo: (state, action) => {
      const data = action.payload;
      // ✅ Transform raw data to normalized format
      if (data) {
        state.companyInfo = {
          name: data.CompanyName || data.Name || data.name || '',
          storeName: data.StoreName || data.storeName || '',
          vat: data.BTWNumber || data.VATNumber || data.vat || '',
          email: data.Email || data.email || '',
          phone: data.PhoneNumber || data.Phone || data.phone || '',
          website: data.Website || data.website || '',
          address: data.Street || data.Address || data.address || '',
          city: data.City || data.city || '',
          postCode: data.PostCode || data.postCode || '',
          country: data.Country || data.country || '',
          iban: data.IBANumber || data.IBAN || data.Iban || data.iban || '',
          _raw: data
        };
      } else {
        state.companyInfo = null;
      }
      state.loading = false;
      state.error = null;
    },
    clearCompanyInfo: (state) => {
      state.companyInfo = null;
      state.loading = false;
      state.error = null;
    },
    setCompanyLoading: (state, action) => {
      state.loading = action.payload;
    },
    setCompanyError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyInfo.fulfilled, (state, action) => {
        state.companyInfo = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCompanyInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setCompanyInfo, 
  clearCompanyInfo, 
  setCompanyLoading, 
  setCompanyError 
} = companySlice.actions;

export const selectCompanyInfo = (state) => state.company.companyInfo;
export const selectCompanyLoading = (state) => state.company.loading;
export const selectCompanyError = (state) => state.company.error;

export default companySlice.reducer;